import { getJSON, tryGetJSON } from "../API/JSON";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace, replaceTemplateVariables } from "./String";

enum SupportedLanguage {
  de = `de`,
  en = `en`,
  fr = `fr`
}

export class Translator {
  private baseTranslation: Record<string, string>;

  private userTranslation: Record<string, string>;

  private readonly baseLanguageCode = SupportedLanguage.en;

  private userLanguageCode: SupportedLanguage;

  constructor(language = `en`) {
    this.userLanguageCode = SupportedLanguage[language];
  }

  public async initTranslator(): Promise<void> {
    this.isLoading = true;
    await this.loadTranslations();
    Translator.instance = this;
    this.isLoading = false;
  }

  private isLoading = true;

  public get isReady(): boolean {
    return !this.isLoading;
  }

  public getActiveLanguageCode(): string {
    return (this.userLanguageCode ?? this.baseLanguageCode ?? SupportedLanguage.en) as string;
  }

  private localeMap = {
    de: `de-DE`,
    en: `en-GB`,
    fr: `fr-FR`
  };

  public getActiveLocale(): string {
    return this.localeMap[this.getActiveLanguageCode()] ?? `en-GB`;
  }

  public get(
    translationKey: string,
    fallback: string | ((variables?: Record<string, string>) => string) = null,
    variables?: Record<string, string>
  ): string {
    if (this.userTranslation != null) {
      const userTranslatedText = getTranslatedText(this.userTranslation, translationKey, variables);
      if (isNotNullOrWhiteSpace(userTranslatedText)) {
        return userTranslatedText;
      }
    }

    if (this.baseTranslation != null) {
      const baseTranslatedText = getTranslatedText(this.baseTranslation, translationKey, variables);
      if (isNotNullOrWhiteSpace(baseTranslatedText)) {
        return baseTranslatedText;
      }
    }

    if (typeof fallback === `function`) {
      return fallback(variables);
    }

    // eslint-disable-next-line no-console
    console.debug(`[Translator] Translation missing for key: ${translationKey}`);
    return `~${fallback}~`;
  }

  public getShortTranslation(longText: string, fallbackText: string): string {
    const shortText = `${longText}.short`;
    return this.get(shortText, () => this.get(longText, fallbackText));
  }

  private async loadTranslations() {
    const loadBaseTranslation = getTranslationLoadRequest(this.baseLanguageCode);

    let loadUserTranslation = Promise.resolve(null);
    if (this.baseLanguageCode !== this.userLanguageCode) {
      loadUserTranslation = getTranslationLoadRequest(this.userLanguageCode);
    }

    const results = await getAllResults(loadBaseTranslation, loadUserTranslation);
    const [baseTranslation, userTranslation] = results;

    this.baseTranslation = normalizeTranslation(baseTranslation);
    this.userTranslation = normalizeTranslation(userTranslation);
  }

  private static instance: Translator = null;

  public static getInstance(): Translator {
    return Translator.instance;
  }
}

function normalizeTranslation(translations: Record<string, string>): Record<string, string> {
  if (translations == null) {
    return translations;
  }

  return Object.fromEntries(Object.entries(translations).map(([key, value]) => [key.toLowerCase(), value]));
}

function getTranslatedText(
  translation: Record<string, string>,
  translationKey: string,
  variables?: Record<string, string>
) {
  if (isNullOrWhiteSpace(translationKey)) {
    return null;
  }
  return replaceTemplateVariables(translation[translationKey.toLowerCase()], variables);
}

async function getAllResults(...promises: Promise<Record<string, string>>[]) {
  const results = await Promise.allSettled(promises);
  return results.map((result) => getPromiseSettledResultValue(result));
}

function getPromiseSettledResultValue(result: PromiseSettledResult<Record<string, string>>): Record<string, string> {
  if (result.status !== `fulfilled`) {
    return null;
  }

  return result.value;
}

const TRANSLATION_REQUEST_CACHE = new Map();

function getTranslationLoadRequest(languageCode: SupportedLanguage): Promise<Record<string, string>> {
  if (!TRANSLATION_REQUEST_CACHE.has(languageCode)) {
    TRANSLATION_REQUEST_CACHE.set(languageCode, loadTranslationFile(languageCode));
  }

  return TRANSLATION_REQUEST_CACHE.get(languageCode);
}

async function loadTranslationFile(languageCode: SupportedLanguage): Promise<Record<string, string>> {
  let baseTranslation = {};

  try {
    baseTranslation = await getJSON<Record<string, string>>(`./config/Translations.default.${languageCode}.json`);
  } catch (e) {
    console.error(`Base translation file for language code "${languageCode}" not available.`, e);
  }

  const customTranslation = await tryGetJSON<Record<string, string>>(
    `./config/custom/Translations.${languageCode}.json`,
    `ℹ️ "./config/custom/Translations.${languageCode}.json" is optional.`
  );

  return {
    ...baseTranslation,
    ...customTranslation
  };
}

export function getBrowserLanguage(): string {
  const preferredLanguageTag = navigator.languages?.[0];
  const languageTag = preferredLanguageTag ?? navigator.language;
  return languageTag.split(`-`).shift();
}
